import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { protocolsI18n } from './i18n';
import {
  getCallList,
  getSummaryFeedBack,
} from '../../services/protocolService';
import VerCallDetailModal from './VerCallDetailModal';
import './protocolGeneralDetail.scss';

const ProtocolGeneralDetail = ({ DesignSystemComponents }) => {
  const { Card, Table, Button, IconButton, Modal } =
    DesignSystemComponents || {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [summaryFeedback, setSummaryFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [callList, setCallList] = useState([]);
  const [callListLoading, setCallListLoading] = useState(false);
  const [callListError, setCallListError] = useState(null);
  const [showCallDetailModal, setShowCallDetailModal] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(null);

  const { language } = useLanguage();
  const prLang = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: protocolsI18n });
  useEffect(() => {
    protocolsI18n.changeLanguage(prLang);
  }, [prLang]);

  const recordId = searchParams.get('id');

  const normalizeSummaryFeedback = (payload) => {
    if (!payload) return null;
    if (payload.overallCompliancePercent !== undefined) return payload;
    if (payload.data?.overallCompliancePercent !== undefined)
      return payload.data;
    if (payload.data?.data?.overallCompliancePercent !== undefined) {
      return payload.data.data;
    }
    return payload;
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadSummary = async () => {
      if (!recordId) {
        setSummaryFeedback(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getSummaryFeedBack(recordId, controller.signal);
        setSummaryFeedback(normalizeSummaryFeedback(response));
      } catch (fetchError) {
        console.error('Error loading summary feedback:', fetchError);
        setError(t('pr_detail_error_load_summary'));
        setSummaryFeedback(null);
      } finally {
        setLoading(false);
      }
    };

    loadSummary();

    return () => controller.abort();
  }, [recordId, t]);

  const normalizeCallList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadCallList = async () => {
      if (!recordId) {
        setCallList([]);
        return;
      }

      try {
        setCallListLoading(true);
        setCallListError(null);
        const response = await getCallList(recordId, controller.signal);
        setCallList(normalizeCallList(response));
      } catch (fetchError) {
        console.error('Error loading call list:', fetchError);
        setCallListError(t('pr_detail_error_load_calls'));
        setCallList([]);
      } finally {
        setCallListLoading(false);
      }
    };

    loadCallList();

    return () => controller.abort();
  }, [recordId, t]);

  const scoreText = useMemo(() => {
    const score = summaryFeedback?.overallCompliancePercent;
    if (score === null || score === undefined || score === '') return '--';
    const cleaned =
      typeof score === 'string' ? score.replace(/[^\d.-]/g, '') : score;
    const asNumber = Number(cleaned);
    if (Number.isNaN(asNumber)) return String(score);
    return `${Math.floor(asNumber)}%`;
  }, [summaryFeedback]);

  const scoreBadge = useMemo(() => {
    const score = Number(summaryFeedback?.overallCompliancePercent);
    if (Number.isNaN(score)) return null;
    if (score >= 90) return { key: 'pr_detail_badge_excellent', color: 'success' };
    if (score >= 80) return { key: 'pr_detail_badge_good', color: 'success' };
    if (score >= 70) return { key: 'pr_detail_badge_acceptable', color: 'warning' };
    return { key: 'pr_detail_badge_low', color: 'danger' };
  }, [summaryFeedback]);

  const columns = [
    { accessor: 'stepName', label: t('pr_detail_col_step') },
    {
      accessor: 'approvalPercentage',
      label: t('pr_detail_col_result'),
      render: (row) => {
        const value = row.approvalPercentage;
        if (value === null || value === undefined || value === '') return '--';
        const asNumber = Number(value);
        if (Number.isNaN(asNumber)) return `${value}%`;
        return `${Math.floor(asNumber)}%`;
      },
    },
    {
      accessor: 'description',
      label: t('pr_detail_col_notes'),
      render: (row) => (
        <span className='protocol-step-notes' title={row.description || '-'}>
          {row.description || '-'}
        </span>
      ),
    },
  ];

  const protocolSteps = Array.isArray(summaryFeedback?.protocolSteps)
    ? summaryFeedback.protocolSteps
    : [];

  const formatCallPercent = (value) => {
    if (value === null || value === undefined || value === '') return '--';
    const asNumber = Number(value);
    if (Number.isNaN(asNumber)) return `${value}%`;
    return `${Math.floor(asNumber)}%`;
  };

  const formatCallDate = (value) =>
    value ? new Date(value).toLocaleString('es-CL') : '--';

  const handleOpenCallDetail = (row) => {
    if (!row?.callId) return;
    setSelectedCallId(row.callId);
    setShowCallDetailModal(true);
  };

  const handleCloseCallDetail = () => {
    setShowCallDetailModal(false);
    setSelectedCallId(null);
  };

  const handleOpenCallDetailFull = (callId) => {
    if (!callId) return;
    navigate(`/crm/detalles-llamada/${callId}`);
  };

  const callColumns = [
    {
      accessor: 'date',
      label: t('pr_detail_col_date'),
      render: (row) => formatCallDate(row.date),
    },
    {
      accessor: 'overallCompliancePercent',
      label: t('pr_detail_col_result'),
      render: (row) => formatCallPercent(row.overallCompliancePercent),
    },
    {
      accessor: 'action',
      render: (row) =>
        IconButton ? (
          <IconButton
            ariaLabel={t('pr_detail_btn_view_detail')}
            icon='eye'
            size='md'
            onClick={() => handleOpenCallDetail(row)}
          />
        ) : Button ? (
          <Button
            label={t('pr_detail_btn_view_detail')}
            size='sm'
            variant='secondary'
            onClick={() => handleOpenCallDetail(row)}
          />
        ) : (
          <button
            type='button'
            className='btn btn-secondary btn-sm'
            onClick={() => handleOpenCallDetail(row)}
          >
            {t('pr_detail_btn_view_detail')}
          </button>
        ),
    },
  ];

  if (!Card || !Table) return null;

  return (
    <div className='protocols-page'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        {Button ? (
          <Button
            variant='naked'
            iconStart='chevronLeft'
            label={t('pr_detail_btn_back')}
            size='sm'
            onClick={() => navigate(-1)}
          />
        ) : (
          <button
            type='button'
            className='btn btn-link p-0'
            onClick={() => navigate(-1)}
          >
            {t('pr_detail_btn_back')}
          </button>
        )}
      </div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('pr_detail_page_title')}</h2>
          <h4 className='fw-normal'>{t('pr_detail_page_subtitle')}</h4>
        </div>
      </div>

      {!recordId && (
        <p className='color-gray-600 mb-4'>{t('pr_detail_no_record')}</p>
      )}
      {error && <p className='text-danger mb-4'>{error}</p>}

      <Card specialPadding='px-4 pt-4 pb-4' className='mb-4'>
        <div className='protocol-general-summary'>
          <div className='protocol-summary-content'>
            <h3 className='fw-semibold mb-2'>{t('pr_detail_summary_title')}</h3>
            <p className='color-gray-600 mb-0'>
              {loading
                ? t('pr_detail_loading_summary')
                : summaryFeedback?.generalSummary ||
                  t('pr_detail_no_summary')}
            </p>
          </div>
          <div className='protocol-summary-score'>
            <div className='protocol-score-circle'>{scoreText}</div>
            {scoreBadge && (
              <span className={`badge bg-${scoreBadge.color} mt-2`}>
                {t(scoreBadge.key)}
              </span>
            )}
          </div>
        </div>
      </Card>

      <Card specialPadding='px-4 pt-4 pb-2'>
        <div className='mb-3'>
          <h3 className='fw-semibold mb-0'>{t('pr_detail_steps_title')}</h3>
        </div>
        <div className='table-container table-general'>
          <Table columns={columns} data={protocolSteps} />
        </div>
      </Card>

      <Card specialPadding='px-4 pt-4 pb-2' className='mt-4'>
        <div className='mb-3'>
          <h3 className='fw-semibold mb-0'>{t('pr_detail_calls_title')}</h3>
        </div>
        {callListError && <p className='text-danger mb-3'>{callListError}</p>}
        <div className='table-container calls-list'>
          <Table columns={callColumns} data={callList} />
        </div>
        {!callListLoading && callList.length === 0 && (
          <div className='text-center p-3'>
            <p className='caption font-light'>
              {t('pr_detail_no_calls')}
            </p>
          </div>
        )}
      </Card>

      <VerCallDetailModal
        Modal={Modal}
        Table={Table}
        show={showCallDetailModal}
        callId={selectedCallId}
        onClose={handleCloseCallDetail}
        onOpenDetail={handleOpenCallDetailFull}
      />
    </div>
  );
};

export default ProtocolGeneralDetail;
